/**
 * «Здоровье канала» — первый экран и единственный бесплатный.
 *
 * Порядок на экране ровно такой, в каком человек задаёт вопросы:
 * 1. Канал в порядке?           — вердикт крупно, одной фразой;
 * 2. Где именно просело?        — области, и проблемные стоят первыми;
 * 3. Что требует внимания?      — сигналы, самые важные сверху;
 * 4. Что проверить?             — у каждого сигнала своя строка действия;
 * 5. Как шло дело?              — график под словами, с выбором показателя;
 * 6. Чему сегодня верить?       — ограничения данных, последним и тише всех.
 *
 * Чего здесь нет намеренно: двадцати одинаково важных плиток. Если всё
 * одинаково важно, взгляду не за что зацепиться, и экран перестаёт работать.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Health, type HealthArea, type HealthSignal, type SparkPoint } from "../lib/api";
import { useSession } from "../session";
import {
  Badge,
  Card,
  ConfidenceBadge,
  Delta,
  Empty,
  ErrorBox,
  LoadingDashboard,
  MetricCard,
  Notice,
  SegmentedControl,
  SeverityBadge,
  StatusBadge,
  Why,
  statusWord,
} from "../components/ui";
import { ChartCard, TimeSeriesChart, type ChartSeries } from "../components/charts";
import { IconCheck, IconRefresh } from "../components/icons";
import { ago, dateTime, decimal, num, pct, plural, signedPct, stateWord } from "../lib/format";

// Порядок областей на экране: проблема важнее нормы. Алфавит и порядок
// ответа сервера тут ни при чём — важен вопрос «куда смотреть первым».
const STATUS_RANK: Record<string, number> = { problem: 0, attention: 1, unknown: 2, ok: 3 };

/** Ряд из спарклайна, который относится к области. Нет ряда — нет и графика:
 *  рисовать «примерно так» под точным числом нельзя. */
const AREA_SERIES: Record<string, (point: SparkPoint) => number | null> = {
  reach: (point) => point.views_median,
  audience: (point) => point.subscribers,
  engagement: (point) => point.reaction_rate,
  ad_load: (point) => (point.posts_total ? (point.posts_ads / point.posts_total) * 100 : null),
};

/** Куда идти разбираться с этой областью. */
const AREA_LINK: Record<string, string> = {
  reach: "/content",
  audience: "/audience",
  engagement: "/content",
  automation: "/settings",
  ad_load: "/ads",
};

const HIGHER_IS_BETTER: Record<string, boolean> = {
  reach: true,
  audience: true,
  engagement: true,
  ad_load: false,
};

export default function HealthPage() {
  const { channelId, channel, can } = useSession();
  const queryClient = useQueryClient();

  const health = useQuery({
    queryKey: ["health", channelId],
    queryFn: () => api.get<Health>(`/health/channel/${channelId}`),
    enabled: Boolean(channelId),
  });

  const refresh = useMutation({
    mutationFn: () => api.post(`/jobs/run/daily?channel_id=${channelId}`),
    onSuccess: () => {
      // Пересчёт идёт в фоне: просить сервер сразу же бессмысленно, поэтому
      // просто помечаем данные устаревшими и обновляем через полминуты.
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["health", channelId] }), 30_000);
    },
  });

  if (!channelId) {
    return (
      <Card>
        <Empty
          title="Каналов пока нет"
          actions={
            <Link to="/channels">
              <button className="primary">Подключить канал</button>
            </Link>
          }
        >
          Подключите канал — из Tokel, напрямую с площадки или демонстрационный, чтобы
          посмотреть продукт без единого ключа.
        </Empty>
      </Card>
    );
  }
  if (health.isLoading) return <LoadingDashboard />;
  if (health.error) return <ErrorBox error={health.error} />;
  const data = health.data!;

  const alerts = data.signals.filter((signal) => signal.severity === "alert");
  const warns = data.signals.filter((signal) => signal.severity === "warn");
  const infos = data.signals.filter((signal) => signal.severity === "info");
  const toCheck = alerts.length + warns.length;
  const limits = data.quality.filter((check) => check.status !== "ok");

  const areas = [...data.areas].sort(
    (left, right) => (STATUS_RANK[left.status] ?? 9) - (STATUS_RANK[right.status] ?? 9),
  );
  const troubled = areas.filter((area) => area.status === "problem" || area.status === "attention");
  const calm = areas.filter((area) => area.status !== "problem" && area.status !== "attention");

  return (
    <>
      {/* Главный вывод. Одна фраза, дальше — доказательства. */}
      <section className={`verdict ${data.status}`}>
        <span className={`status-dot lg ${data.status}`} />
        <div className="verdict-body">
          <h1>{data.headline}</h1>
          <div className="sub">
            {channel?.title} · состояние на {dateTime(data.computed_at)}
            {data.previous_status && data.previous_status !== data.status && (
              <> · вчера было «{statusWord(data.previous_status)}»</>
            )}
          </div>
          {toCheck > 0 && (
            <div className="row" style={{ marginTop: 14, gap: 8 }}>
              {alerts.length > 0 && (
                <Badge tone="alert">
                  {alerts.length} {plural(alerts.length, "требует", "требуют", "требуют")} вмешательства
                </Badge>
              )}
              {warns.length > 0 && <Badge tone="warn">{warns.length} к проверке</Badge>}
              {infos.length > 0 && <Badge tone="muted">{infos.length} к сведению</Badge>}
            </div>
          )}
        </div>
        <div className="verdict-aside">
          <StatusBadge status={data.status} />
          {can("run_jobs") && (
            <button className="small" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
              <IconRefresh size={15} />
              {refresh.isPending ? "Запущено" : "Пересчитать"}
            </button>
          )}
        </div>
      </section>

      {refresh.isSuccess && (
        <Notice>Пересчёт запущен в фоне. Числа обновятся сами через минуту-другую.</Notice>
      )}

      {/* Области. Проблемные крупнее и первыми — остальные ровным рядом ниже. */}
      {troubled.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))" }}>
          {troubled.map((area) => (
            <AreaCard key={area.key} area={area} sparkline={data.sparkline} />
          ))}
        </div>
      )}
      {calm.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(168px, 1fr))", gap: "var(--s3)" }}>
          {calm.map((area) => (
            <AreaCard key={area.key} area={area} sparkline={data.sparkline} quiet />
          ))}
        </div>
      )}
      {/* Подпись одна на весь ряд: «к прошлым двум неделям» на каждой из шести
          карточек — это пять лишних прочтений одного и того же. */}
      <p className="faint" style={{ marginTop: "calc(var(--s4) * -1)" }}>
        Изменение — к предыдущим двум неделям. Норма считается по самому каналу, а не по «рынку».
      </p>

      <Card
        title="Что требует внимания"
        note={
          data.signals.length === 0
            ? "Ничего — и это тоже результат"
            : `${toCheck} к проверке · ${infos.length} к сведению`
        }
      >
        {data.signals.length === 0 ? (
          <div className="state" style={{ padding: "var(--s6)" }}>
            <span className="state-icon" style={{ background: "var(--positive-soft)", color: "var(--positive)" }}>
              <IconCheck size={22} />
            </span>
            <h3>Отклонений не нашлось</h3>
            <p>Сравнение идёт с собственной нормой канала за последние 28 дней, а не с «рынком».</p>
          </div>
        ) : (
          <div>
            {[...alerts, ...warns, ...infos].map((signal) => (
              <SignalRow key={signal.key} signal={signal} />
            ))}
          </div>
        )}
      </Card>

      <TrendCard sparkline={data.sparkline} />

      <Card
        title="Чему сегодня можно верить"
        note={limits.length ? `${limits.length} ${plural(limits.length, "ограничение", "ограничения", "ограничений")}` : "ограничений нет"}
      >
        {data.quality.length === 0 ? (
          <p className="muted">Проверки качества данных по этому каналу ещё не считались.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Проверка</th>
                  <th>Состояние</th>
                  <th>Ожидание</th>
                  <th>Что это значит</th>
                </tr>
              </thead>
              <tbody>
                {data.quality.map((check) => (
                  <tr key={check.key}>
                    <td>{check.title}</td>
                    <td>
                      <Badge tone={check.status === "ok" ? "ok" : check.status === "warn" ? "warn" : "alert"}>
                        {check.status === "ok" ? "в норме" : check.status === "warn" ? "неполно" : "плохо"}
                      </Badge>
                    </td>
                    <td className="dim">{check.expected}</td>
                    <td className="dim">{check.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Why summary="Когда данные обновлялись">
          {data.jobs.length === 0 ? (
            <p>Фоновые задачи по этому каналу ещё не запускались.</p>
          ) : (
            <div className="table-wrap">
              <table className="facts">
                <tbody>
                  {data.jobs.map((job, index) => (
                    <tr key={`${job.job}-${index}`}>
                      <td className="mono" style={{ width: "34%" }}>
                        {job.job}
                      </td>
                      <td style={{ width: "20%" }}>
                        <Badge tone={job.status === "ok" ? "ok" : job.status === "error" ? "alert" : "muted"}>
                          {stateWord(job.status)}
                        </Badge>
                      </td>
                      <td className="dim">
                        {ago(job.started_at)}
                        {job.error && <div className="faint">{job.error.slice(0, 120)}</div>}
                      </td>
                      <td className="num dim">{job.duration_ms === null ? "—" : `${num(job.duration_ms)} мс`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Why>
      </Card>
    </>
  );
}

// ── Область ─────────────────────────────────────────────────────────────

function AreaCard({
  area,
  sparkline,
  quiet = false,
}: {
  area: HealthArea;
  sparkline: SparkPoint[];
  quiet?: boolean;
}) {
  const pull = AREA_SERIES[area.key];
  const spark = pull ? sparkline.map(pull) : undefined;
  const link = AREA_LINK[area.key];

  const card = (
    <MetricCard
      name={area.title}
      small={quiet}
      title={quiet ? area.summary : undefined}
      status={area.status as HealthArea["status"]}
      value={area.value === null ? "—" : formatArea(area.unit, area.value)}
      unit={area.value === null ? undefined : shortUnit(area.unit)}
      delta={
        area.delta_pct !== null && area.value !== null ? (
          <Delta value={area.delta_pct} higherIsBetter={HIGHER_IS_BETTER[area.key] ?? true} />
        ) : undefined
      }
      context={quiet ? undefined : area.summary}
      spark={quiet ? undefined : spark}
      sparkColor={
        area.status === "problem"
          ? "var(--negative)"
          : area.status === "attention"
            ? "var(--attention)"
            : "var(--chart-1)"
      }
    />
  );

  if (!link) return card;
  return (
    <Link to={link} style={{ display: "block", color: "inherit" }} title={`Разобраться: ${area.title}`}>
      {card}
    </Link>
  );
}

// ── График ──────────────────────────────────────────────────────────────

type TrendKey = "views" | "subscribers" | "engagement" | "posts";

/**
 * График под словами, а не вместо них. Показатель переключается, потому что
 * вопросов к одному и тому же ряду дней четыре, а не один.
 */
function TrendCard({ sparkline }: { sparkline: SparkPoint[] }) {
  const [metric, setMetric] = useState<TrendKey>("views");
  const dates = sparkline.map((point) => point.date);

  const series: Record<TrendKey, { series: ChartSeries[]; format: (value: number) => string; note: string }> = {
    views: {
      series: [
        {
          key: "views",
          label: "Просмотры обычной публикации",
          color: "var(--chart-1)",
          kind: "area",
          values: sparkline.map((point) => point.views_median),
        },
        {
          key: "views24",
          label: "Просмотры к 24 часам",
          color: "var(--chart-3)",
          values: sparkline.map((point) => point.views_24h_median),
        },
      ],
      format: (value) => num(Math.round(value)),
      note: "Медиана по редакционным публикациям, дожившим до срока дозревания. Разрыв в линии — сутки, в которые дозревших публикаций не было; ноль туда рисовать нельзя.",
    },
    subscribers: {
      series: [
        {
          key: "subs",
          label: "Подписчики",
          color: "var(--chart-1)",
          kind: "area",
          values: sparkline.map((point) => point.subscribers),
        },
      ],
      format: (value) => num(Math.round(value)),
      note: "Замер на конец суток. До подключения канала числа подписчиков не существует ни на одной площадке.",
    },
    engagement: {
      series: [
        {
          key: "reaction",
          label: "Реакций на 1000 просмотров",
          color: "var(--chart-1)",
          kind: "area",
          values: sparkline.map((point) => point.reaction_rate),
        },
      ],
      format: (value) => decimal(value, 1),
      note: "Отклик считается на тысячу просмотров, а не на публикацию: иначе показатель растёт просто от роста охвата.",
    },
    posts: {
      series: [
        {
          key: "all",
          label: "Всего публикаций",
          color: "var(--chart-2)",
          kind: "bar",
          values: sparkline.map((point) => point.posts_total),
        },
        {
          key: "ads",
          label: "Из них рекламных",
          color: "var(--chart-1)",
          kind: "bar",
          values: sparkline.map((point) => point.posts_ads),
        },
      ],
      format: (value) => num(value),
      note: "Столбики — счётная величина за сутки, поэтому здесь столбики, а не линия.",
    },
  };

  const current = series[metric];

  return (
    <ChartCard
      title="Как шли дела"
      sub="Последние 30 суток. График — доказательство под словами выше, а не отдельный продукт."
      controls={
        <SegmentedControl
          value={metric}
          onChange={setMetric}
          ariaLabel="Показатель"
          options={[
            { value: "views", label: "Просмотры" },
            { value: "subscribers", label: "Подписчики" },
            { value: "engagement", label: "Отклик" },
            { value: "posts", label: "Публикации" },
          ]}
        />
      }
      series={current.series}
      footnote={current.note}
    >
      <TimeSeriesChart
        dates={dates}
        series={current.series}
        formatTip={current.format}
        includeZero={metric === "posts"}
        emptyNote="За последние 30 суток по этому показателю нет ни одного измерения."
      />
    </ChartCard>
  );
}

// ── Сигнал ──────────────────────────────────────────────────────────────

function SignalRow({ signal }: { signal: HealthSignal }) {
  return (
    <div className={`signal ${signal.severity}`}>
      <span className="rail" />
      <div className="signal-body">
        <div className="what">{signal.what}</div>
        <div className="check">{signal.check}</div>
        <div className="meta">
          <SeverityBadge level={signal.severity} />
          <Badge tone="muted">{signal.area_title}</Badge>
          <ConfidenceBadge level={signal.confidence} reason={signal.confidence_reason} />
        </div>
        {(signal.confidence_reason || Object.keys(signal.facts).length > 0) && (
          <Why summary="Из чего это посчитано">
            {signal.confidence_reason && <p style={{ marginBottom: 10 }}>{signal.confidence_reason}</p>}
            <FactList facts={signal.facts} />
            <p style={{ marginTop: 10 }} className="faint">
              Совпадение по времени не означает причину. Здесь показано, что изменилось, а не почему.
            </p>
          </Why>
        )}
      </div>
    </div>
  );
}

const FACT_LABELS: Record<string, string> = {
  before: "было",
  after: "стало",
  delta_pct: "изменение",
  days_before: "суток в прошлом периоде",
  days_after: "суток в текущем",
  p_value: "вероятность случайности",
  metric_title: "показатель",
  sample_size: "размер выборки",
  share_pct: "доля",
  missed: "пропущено",
  planned: "запланировано",
  subscribers: "подписчиков",
  median_daily_delta: "обычный прирост в сутки",
  change_14d: "изменение за 14 дней",
  value: "значение",
  baseline: "норма",
  baseline_high: "верх обычного размаха",
  duplicates: "повторов",
  groups: "групп",
  hours: "часов",
  days: "дней",
  needed: "нужно",
  strata: "сопоставимых групп",
  system_median: "медиана автопилота",
  admin_median: "медиана собственных",
  system_n: "публикаций автопилота",
  admin_n: "собственных публикаций",
  typical_usd: "обычно, $",
  latest_usd: "сейчас, $",
};

export function FactList({ facts }: { facts: Record<string, unknown> }) {
  const entries = Object.entries(facts).filter(([, value]) => value !== null && value !== undefined);
  if (!entries.length) return null;
  return (
    <div className="table-wrap">
      <table className="facts">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key}>
              <td className="dim" style={{ width: "55%" }}>
                {FACT_LABELS[key] ?? key}
              </td>
              <td className="mono">{renderFact(key, value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderFact(key: string, value: unknown): string {
  if (typeof value === "number") {
    if (key.endsWith("_pct")) return signedPct(value, 1);
    if (key === "p_value") return decimal(value, 3);
    if (Number.isInteger(value)) return num(value);
    return decimal(value, 2);
  }
  if (typeof value === "boolean") return value ? "да" : "нет";
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatArea(unit: string, value: number): string {
  // Проценты — без десятых: подпись под карточкой округляет так же, и «10,4%»
  // рядом с «10% публикаций» читалось бы как два разных числа.
  if (unit === "%") return pct(value, 0);
  if (unit === "на 1000") return decimal(value, 1);
  return num(Math.round(value));
}

/** Единица рядом со значением — только там, где она не удваивает название.
 *  «Охват · 12 400 просмотров» полезно, «Рекламная нагрузка · 12% %» — нет. */
function shortUnit(unit: string): string | undefined {
  if (!unit || unit === "%") return undefined;
  return unit;
}
